// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Type definition for expected format of product data received from the
 * server.
 * @author James Reid
 */

// @ts-check

// @@imports-types
/* eslint-disable no-unused-vars -- Types only used in comments. */
import { Unit } from "./Units.js"
/* eslint-enable no-unused-vars -- Close disable-enable pair. */

// @@body
/**
 * Product data object received from Roof-Calc server.
 * @typedef {object} ProductData
 * @property {string} uuid UUID of product in the database.
 * @property {ProductDataSkus} skus System SKUs of product.
 * @property {string} name Product name.
 * @property {string} description Long product description.
 * @property {string} type Product type.
 * @property {string[]} categories Product categories under main type.
 * @property {number?} stock Currently product stock available to calculator.
 * @property {number} price Price per unit.
 * @property {string} currency Product retail price currency stored as 3-char
 * ISO 4217 string. See [here](https://en.wikipedia.org/wiki/ISO_4217) for spec.
 * @property {ProductDataDimensions} dimensions Dimensions of product.
 * @property {ProductDataUrls} urls URLs relating to product.
 */

/**
 * @typedef {object} ProductDataSkus
 * @property {string} product FlexiProof product SKU.
 * @property {string?} merchant Merchant SKU corresponding to the given product.
 * If unset, defaults to FlexiProof SKU when required in calculator.
 * @property {string} calculator Internal calculator SKU code.
 */

/**
 * @typedef {object} ProductDataDimensions
 * @property {number?} width Product width dimension in SI units.
 * @property {number?} height Product height dimension in SI units.
 * @property {number?} depth Product depth dimension in SI units.
 * @property {number?} length Product length dimension in SI units.
 * @property {number?} thickness Product thickness dimension in mm.
 * @property {number?} volume Product volume dimension in SI units.
 * @property {number?} weight Product width dimension in SI units.
 */

/**
 * @typedef {object} ProductDataUrls
 * @property {string} image Product image URL.
 * @property {string} product FlexiProof product URL.
 * @property {string} installation EPDM Academy product installation video URL.
 */

// @@exports
/**
 * @type {ProductData}
 * @ignore
 */
export let ProductData
